export interface Faq {
    id: number;
    question: string;
    answer: string;
}

const faqs: Faq[] = [
    {
        id: 1,
        question: "Can I use StudyBuddy on multiple devices?",
        answer: "Absolutely! StudyBuddy is accessible on desktop, tablet, and mobile devices through your web browser. Your progress syncs across all platforms for a seamless experience. A dedicated mobile app is in development to further enhance your use of StudyBuddy on the go!"
    },
    {
        id: 2,
        question: "Can I cancel my subscription at any time?",
        answer: "Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period."
    },
    {
        id: 3,
        question: "Is there a refund policy?",
        answer: "Refunds are handled on a case-by-case basis. For assistance, please contact our support team. You can typically find contact options on our main website or through your account dashboard."
    },
    {
        id: 4,
        question: "Can I upgrade or downgrade my plan?",
        answer: "Yes, you can change your plan at any time. When upgrading, you'll have immediate access to new features. When downgrading, changes will take effect at the start of your next billing cycle."
    },
    {
        id: 5,
        question: "What are Spaces?",
        answer: "Spaces are personalized environments where you can organize your materials, upload PDFs, and have focused AI conversations around specific topics or projects. Each space can contain multiple chats, uploaded documents, and automatically generated outputs like summaries, code examples, and visualizations. It's like having a dedicated virtual workspace for each topic or project."
    },
    {
        id: 6,
        question: "How do Spaces help you work effectively?",
        answer: "Spaces enhance your workflow by: 1) Organizing related materials and conversations in one place, 2) Allowing PDF uploads for AI-assisted document analysis, 3) Generating useful outputs like summaries and explanations, 4) Enabling focused discussions around specific topics, and 5) Tracking your progress across different projects or areas of interest."
    },
    {
        id: 7,
        question: "What's the difference between regular chats and Spaces?",
        answer: "While regular chats are great for quick questions and general information retrieval, Spaces provide a structured environment for deeper, long-term projects and exploration. They allow you to upload relevant documents, maintain context across multiple conversations, and automatically organize generated content. Think of regular chats as quick AI consultations and Spaces as dedicated virtual workspaces."
    },
    {
        id: 8,
        question: "What features are included in the Free plan?",
        answer: `Our Free plan offers a great way to get started with StudyBuddy. It includes:
- Access to core AI chat capabilities (up to 150 messages on a rolling 30-day basis)
- Up to 3 file uploads per chat
- AI-powered Quiz Generation
- Up to 10 AI image generations on a rolling 30-day basis.`
    },
    {
        id: 9,
        question: "What are the benefits of upgrading to the Premium plan?",
        answer: `The Premium plan enhances your experience with:
- Increased AI chat messages (up to 1500 on a rolling 30-day basis).
- Ability to upload up to 10 files directly within each regular chat conversation.
- More AI image generations (up to 75 on a rolling 30-day basis).
- Continued access to Quiz Generation and core AI capabilities.`
    },
    {
        id: 10,
        question: "What does the Ultimate plan offer?",
        answer: `The Ultimate plan is our most comprehensive package, including:
- Everything in Premium, plus:
- The highest AI chat message limit (up to 2500 on a rolling 30-day basis).
- Increased file uploads (up to 20 files directly within each regular chat conversation).
- The most AI image generations (up to 150 on a rolling 30-day basis).
- Exclusive access to **Spaces** – your personalized environments for organizing materials (PDFs up to 5MB each, with a total knowledge capacity per Space), uploading PDFs for AI analysis, and focused discussions.
- Early access to beta features.
- A 7-day free trial to start.`
    },
    {
        id: 11,
        question: "Is there a free trial available?",
        answer: "Yes. Premium and Ultimate both start with a 7-day free trial. You won't be charged until the trial ends, and you can cancel from the billing portal before then."
    },
    {
        id: 12,
        question: 'How do the "rolling 30-day" message and image generation limits work?',
        answer: "Your monthly allowance for chat messages and image generations operates on a rolling 30-day basis. This means your limit doesn't reset on a fixed day of the month for all users. Instead, your allowance for the next 30 days begins from the day you first use a message or generate an image in that cycle. For example, if you use your first message on June 5th, that cycle's allowance runs for 30 days from June 5th."
    },
    {
        id: 13,
        question: "How does billing work for annual plans?",
        answer: "If you choose an annual plan, you'll be billed for the entire year upfront at a discounted rate compared to paying monthly. You can see the total annual cost on our [pricing page](/pricing) when you select annual billing."
    },
    {
        id: 14,
        question: "How can I manage my subscription or change my plan?",
        answer: "You can manage your subscription, including changing your plan or updating billing details, through our Stripe customer portal. When you're signed in, use **Manage plan** on the [pricing page](/pricing) or **Manage Subscription** in Settings to open the portal."
    },
    {
        id: 15,
        question: "What are AI Image Generations?",
        answer: "Our AI can generate images based on your textual descriptions. This feature, available on all plans with varying limits, allows you to visualize concepts, create illustrations for your projects, or simply explore creative ideas. The number of images you can generate per 30-day cycle depends on your subscription plan."
    },
    {
        id: 16,
        question: 'What does "Access to beta features" on the Ultimate plan mean?',
        answer: "As an Ultimate subscriber, you'll get early access to new and experimental StudyBuddy features before they are released to other users. This allows you to try out our latest innovations and provide feedback to help shape the future of the platform."
    },
    {
        id: 17,
        question: "What are the limits for uploading Materials to a Space?",
        answer: `When adding Materials (files) to your Spaces:
- You can upload PDF files (.pdf).
- Each individual file can be up to 5MB in size.
- Each Space has a total "Knowledge Capacity" for all uploaded files (e.g., around 30MB per Space). You can see how much capacity you've used within each Space. If a Space's capacity is full, you'll need to remove some existing files or create a new Space to add more.`
    }
];

export default faqs;